/**
 * MAIN APP COMPONENT
 *
 * LinkedIn Bot Dashboard with Material-UI
 */

import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline, CircularProgress, Box } from "@mui/material";
import { Toaster } from "react-hot-toast";

// Import components
import AuthProvider from "./contexts/AuthContext";
import SocketProvider from "./contexts/SocketContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";

// Lazy load pages
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const AddKeywordPage = React.lazy(() => import("./pages/AddKeywordPage"));
const KeywordListPage = React.lazy(() => import("./pages/KeywordListPage"));
const AnalyticsPage = React.lazy(() => import("./pages/AnalyticsPage"));
const LicensePage = React.lazy(() => import("./pages/LicensePage"));

// Follow-up pages
const FollowUpLikePage = React.lazy(
  () => import("./pages/followup/FollowUpLikePage"),
);
const FollowUpCommentPage = React.lazy(
  () => import("./pages/followup/FollowUpCommentPage"),
);

// Post pages
const PostDraftPage = React.lazy(() => import("./pages/post/PostDraftPage"));
const PostPostedPage = React.lazy(() => import("./pages/post/PostPostedPage"));

// Message pages
const MessageListPage = React.lazy(
  () => import("./pages/message/MessageListPage"),
);
const MessageDirectPage = React.lazy(
  () => import("./pages/message/MessageDirectPage"),
);
const MessageConnectionPage = React.lazy(
  () => import("./pages/message/MessageConnectionPage"),
);

// Create theme
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#0e7490",
      light: "#67b7d1",
      dark: "#164e63",
    },
    secondary: {
      main: "#7c3aed",
      light: "#a855f7",
      dark: "#5b21b6",
    },
    background: {
      default: "#0f172a",
      paper: "#1e293b",
    },
    text: {
      primary: "#f1f5f9",
      secondary: "#cbd5e1",
    },
  },
  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#0f172a",
          backgroundImage: "none",
        },
        html: {
          backgroundColor: "#0f172a",
        },
        "#root": {
          backgroundColor: "#0f172a",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#1e293b",
          border: "1px solid #334155",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

// Loading component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    sx={{
      backgroundColor: "#0f172a",
      width: "100%",
    }}
  >
    <CircularProgress size={60} />
  </Box>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box
            sx={{
              backgroundColor: "#0f172a",
              minHeight: "100vh",
              width: "100%",
            }}
          >
            <AuthProvider>
              <SocketProvider>
                <Router>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />

                      {/* Protected routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <DashboardPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <DashboardPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <SettingsPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings/add-keyword"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <AddKeywordPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings/keyword-list"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <KeywordListPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <AnalyticsPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/license"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <LicensePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Follow-up routes */}
                      <Route
                        path="/followup/like"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <FollowUpLikePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/followup/comment"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <FollowUpCommentPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Post routes */}
                      <Route
                        path="/post/draft"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <PostDraftPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/post/posted"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <PostPostedPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Message routes */}
                      <Route
                        path="/message/list"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <MessageListPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/message/direct"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <MessageDirectPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/message/connection"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <MessageConnectionPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Fallback route: redirect to root (/) */}
                      <Route path="*" element={<DashboardPage />} />
                    </Routes>
                  </Suspense>
                </Router>

                {/* Toast notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "#1e293b",
                      color: "#f1f5f9",
                      border: "1px solid #334155",
                    },
                    success: {
                      iconTheme: {
                        primary: "#10b981",
                        secondary: "#f1f5f9",
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: "#ef4444",
                        secondary: "#f1f5f9",
                      },
                    },
                  }}
                />
              </SocketProvider>
            </AuthProvider>
          </Box>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
